import React, { useEffect, useState } from 'react';
import { plansAPI, drillsAPI } from '../../lib/api';
import Layout from '../../components/Layout';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, Trash2, ChevronDown, ChevronUp, Calendar, Brain, Apple, Timer as TimerIcon } from 'lucide-react';
import { toast } from 'sonner';

const DAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const DEFAULT_TIMER = { enabled: true, sets: 3, reps_per_set: 10, work_seconds: 30, rest_between_reps_seconds: 0, rest_between_sets_seconds: 60 };

const DEFAULT_DAYS = DAY_LABELS.map((label, i) => ({
  day_number: i + 1, title: label, drill_ids: [], drill_timers: {}, mental_tip: '', nutrition_tip: '',
}));

// Format local date as YYYY-MM-DD (no UTC conversion)
function toLocalDateStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Get this Monday's date string
function getThisMonday() {
  const d = new Date();
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return toLocalDateStr(d);
}

// Format date for display
function formatWeekLabel(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  const end = new Date(d);
  end.setDate(end.getDate() + 4);
  const opts = { month: 'short', day: 'numeric' };
  return `${d.toLocaleDateString('en-US', opts)} – ${end.toLocaleDateString('en-US', opts)}`;
}

// Check if a plan is for the current week
function isCurrentWeek(dateStr) {
  if (!dateStr) return false;
  return dateStr === getThisMonday();
}

function isFutureWeek(dateStr) {
  if (!dateStr) return false;
  return dateStr > getThisMonday();
}

export default function CoachPlans() {
  const [plans, setPlans] = useState([]);
  const [drills, setDrills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    week_number: 1, title: '', description: '',
    start_date: getThisMonday(),
    days: DEFAULT_DAYS.map(d => ({ ...d })),
  });
  const [expandedDay, setExpandedDay] = useState(0);
  const [dayTab, setDayTab] = useState('drills'); // 'drills' | 'tips'

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [plansRes, drillsRes] = await Promise.all([plansAPI.list(), drillsAPI.list()]);
      setPlans(plansRes.data || []);
      setDrills(drillsRes.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const resetForm = () => {
    setForm({
      week_number: 1, title: '', description: '',
      start_date: getThisMonday(),
      days: DEFAULT_DAYS.map(d => ({ ...d })),
    });
    setEditingId(null);
    setShowForm(false);
    setExpandedDay(0);
    setDayTab('drills');
  };

  const handleEdit = (plan) => {
    setForm({
      week_number: plan.week_number,
      title: plan.title,
      description: plan.description || '',
      start_date: plan.start_date || '',
      days: plan.days?.length ? plan.days.map(d => ({
        ...d,
        drill_timers: d.drill_timers || {},
        mental_tip: d.mental_tip || '',
        nutrition_tip: d.nutrition_tip || '',
      })) : DEFAULT_DAYS.map(d => ({ ...d, drill_timers: {} })),
    });
    setEditingId(plan.id);
    setShowForm(true);
  };

  const updateDay = (i, field, value) => {
    setForm(f => ({ ...f, days: f.days.map((d, j) => j === i ? { ...d, [field]: value } : d) }));
  };

  const toggleDrill = (dayIndex, drillId) => {
    setForm(f => ({
      ...f,
      days: f.days.map((d, j) => {
        if (j !== dayIndex) return d;
        const has = d.drill_ids.includes(drillId);
        const ids = has ? d.drill_ids.filter(id => id !== drillId) : [...d.drill_ids, drillId];
        // Clean up timer if drill removed
        const nextTimers = { ...(d.drill_timers || {}) };
        if (has) delete nextTimers[drillId];
        return { ...d, drill_ids: ids, drill_timers: nextTimers };
      }),
    }));
  };

  const updateDrillTimer = (dayIndex, drillId, patch) => {
    setForm(f => ({
      ...f,
      days: f.days.map((d, j) => {
        if (j !== dayIndex) return d;
        const prev = (d.drill_timers && d.drill_timers[drillId]) || { ...DEFAULT_TIMER, enabled: false };
        const nextTimer = { ...prev, ...patch };
        return { ...d, drill_timers: { ...(d.drill_timers || {}), [drillId]: nextTimer } };
      }),
    }));
  };

  const toggleDrillTimer = (dayIndex, drillId) => {
    setForm(f => ({
      ...f,
      days: f.days.map((d, j) => {
        if (j !== dayIndex) return d;
        const current = (d.drill_timers && d.drill_timers[drillId]) || null;
        const nextTimer = current && current.enabled
          ? { ...current, enabled: false }
          : { ...DEFAULT_TIMER, ...(current || {}), enabled: true };
        return { ...d, drill_timers: { ...(d.drill_timers || {}), [drillId]: nextTimer } };
      }),
    }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Plan title required'); return; }
    if (!form.start_date) { toast.error('Start date required'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await plansAPI.update(editingId, form);
        toast.success('Plan updated');
      } else {
        await plansAPI.create(form);
        toast.success('Plan scheduled!');
      }
      resetForm();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this plan?')) return;
    try { await plansAPI.delete(id); toast.success('Deleted'); loadData(); } catch { toast.error('Failed'); }
  };

  // Generate Monday date options (this week + next 8 weeks)
  const mondayOptions = [];
  const thisMonday = new Date();
  thisMonday.setDate(thisMonday.getDate() - ((thisMonday.getDay() + 6) % 7));
  for (let i = 0; i < 9; i++) {
    const m = new Date(thisMonday);
    m.setDate(m.getDate() + i * 7);
    mondayOptions.push(toLocalDateStr(m));
  }

  // Sort plans by start_date
  const sortedPlans = [...plans].sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''));

  return (
    <Layout title="PLANS" showBack>
      <div className="px-4 py-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold tracking-tight" data-testid="plans-title">WEEKLY PLANS</h2>
            <p className="text-[10px] text-[#64748B] mt-0.5">Schedule plans ahead — they auto-release on the right week</p>
          </div>
          <button onClick={() => { resetForm(); setShowForm(true); }} className="bg-[#2563EB] hover:bg-[#3B82F6] text-white font-bold uppercase tracking-wide rounded-lg px-4 py-2 text-xs transition-colors flex items-center gap-1" data-testid="add-plan-btn">
            <Plus size={14} /> New
          </button>
        </div>

        <AnimatePresence>
          {showForm && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }} className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-['Barlow_Condensed'] text-base font-bold">{editingId ? 'EDIT PLAN' : 'NEW PLAN'}</h3>
                <button onClick={resetForm} className="text-[#64748B] hover:text-[#0A2242]"><X size={18} /></button>
              </div>

              {/* Title + Week Number */}
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="text-[10px] uppercase tracking-widest text-[#64748B] font-bold mb-1 block">Title</label>
                  <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-4 py-3 text-[#0A2242] text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB] placeholder:text-[#64748B]" placeholder="e.g. Week 1: Foundation" data-testid="plan-title-input" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#64748B] font-bold mb-1 block">Week #</label>
                  <input type="number" value={form.week_number} onChange={e => setForm(f => ({ ...f, week_number: parseInt(e.target.value) || 1 }))} className="w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-4 py-3 text-[#0A2242] text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]" data-testid="plan-week-input" />
                </div>
              </div>

              {/* Start Date */}
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#64748B] font-bold mb-1 block flex items-center gap-1">
                  <Calendar size={10} /> Start Date (Monday)
                </label>
                <input
                  type="date"
                  value={form.start_date}
                  onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                  className="w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-4 py-3 text-[#0A2242] text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
                  data-testid="plan-date-input"
                />
                {form.start_date && (
                  <p className="text-[9px] text-[#64748B] mt-1">
                    {formatWeekLabel(form.start_date)} {isCurrentWeek(form.start_date) ? '— This Week' : ''}
                  </p>
                )}
                {/* Quick Monday shortcuts */}
                <div className="flex gap-1.5 mt-2 flex-wrap">
                  {mondayOptions.slice(0, 5).map((date, i) => (
                    <button
                      key={date}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, start_date: date }))}
                      className={`text-[9px] font-bold px-2.5 py-1.5 rounded-lg transition-colors ${
                        form.start_date === date
                          ? 'bg-[#2563EB] text-white'
                          : 'bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0]'
                      }`}
                    >
                      {i === 0 ? 'This Wk' : `+${i}wk`}
                    </button>
                  ))}
                </div>
              </div>

              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-4 py-3 text-[#0A2242] text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB] placeholder:text-[#64748B] resize-none h-16" placeholder="Description (optional)" data-testid="plan-desc-input" />

              {/* Days (Mon-Fri) */}
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-widest text-[#64748B] font-bold">Days (Mon–Fri)</label>

                {form.days.map((day, di) => (
                  <div key={di} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg overflow-hidden">
                    <button onClick={() => setExpandedDay(expandedDay === di ? -1 : di)} className="w-full flex items-center justify-between p-3 text-left">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-[#00E5FF]">D{day.day_number}</span>
                        <span className="text-sm text-[#0A2242] font-medium">{DAY_LABELS[di]}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-[#64748B]">{day.drill_ids.length} drills</span>
                        {(day.mental_tip || day.nutrition_tip) && (
                          <span className="text-[10px] text-[#10B981]">+ tips</span>
                        )}
                        {expandedDay === di ? <ChevronUp size={14} className="text-[#64748B]" /> : <ChevronDown size={14} className="text-[#64748B]" />}
                      </div>
                    </button>

                    {expandedDay === di && (
                      <div className="p-3 pt-0 space-y-3">
                        {/* Tab switcher */}
                        <div className="flex gap-1 bg-white rounded-lg p-1">
                          {[
                            { id: 'drills', label: 'Drills' },
                            { id: 'tips', label: 'Tips' },
                          ].map(tab => (
                            <button
                              key={tab.id}
                              onClick={() => setDayTab(tab.id)}
                              className={`flex-1 text-[10px] font-bold uppercase tracking-widest py-2 rounded-md transition-colors ${
                                dayTab === tab.id ? 'bg-[#2563EB] text-white' : 'text-[#64748B] hover:text-[#0A2242]'
                              }`}
                            >
                              {tab.label}
                            </button>
                          ))}
                        </div>

                        {dayTab === 'drills' ? (
                          <div className="space-y-3">
                            {/* Selected Drills with inline timer editor */}
                            {day.drill_ids.length > 0 && (
                              <div className="space-y-2">
                                <p className="text-[10px] uppercase tracking-widest text-[#64748B] font-bold">In This Day ({day.drill_ids.length})</p>
                                {day.drill_ids.map(did => {
                                  const drill = drills.find(dr => dr.id === did);
                                  if (!drill) return null;
                                  const timer = (day.drill_timers || {})[did] || null;
                                  const timerOn = !!(timer && timer.enabled);
                                  const t = timer || { ...DEFAULT_TIMER, enabled: false };
                                  return (
                                    <div key={did} className="bg-white border border-[#E2E8F0] rounded-lg p-2" data-testid={`plan-drill-${did}`}>
                                      <div className="flex items-center gap-2">
                                        <button
                                          onClick={() => toggleDrill(di, did)}
                                          className="w-6 h-6 rounded-full bg-[#2563EB]/10 hover:bg-red-400/20 text-[#2563EB] hover:text-red-400 flex items-center justify-center flex-shrink-0 transition-colors"
                                          aria-label="Remove drill"
                                        >
                                          <X size={12} />
                                        </button>
                                        <div className="flex-1 min-w-0">
                                          <p className="text-xs font-bold text-[#0A2242] truncate">{drill.title}</p>
                                          <p className="text-[9px] text-[#64748B]">{drill.category} · {drill.duration_minutes}m</p>
                                        </div>
                                        <button
                                          onClick={() => toggleDrillTimer(di, did)}
                                          className={`flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors ${
                                            timerOn
                                              ? 'bg-[#FF9100] text-white'
                                              : 'bg-[#F1F5F9] text-[#64748B] hover:bg-[#2563EB]/10 hover:text-[#2563EB]'
                                          }`}
                                          data-testid={`drill-timer-toggle-${did}`}
                                        >
                                          <TimerIcon size={10} />
                                          {timerOn ? 'Timer On' : 'Add Timer'}
                                        </button>
                                      </div>

                                      {timerOn && (
                                        <div className="mt-2 grid grid-cols-5 gap-1.5" data-testid={`drill-timer-config-${did}`}>
                                          {[
                                            { key: 'sets', label: 'Sets', min: 1, max: 20 },
                                            { key: 'reps_per_set', label: 'Reps', min: 1, max: 100 },
                                            { key: 'work_seconds', label: 'Work', min: 1, max: 600, suffix: 's' },
                                            { key: 'rest_between_reps_seconds', label: 'RepRst', min: 0, max: 600, suffix: 's' },
                                            { key: 'rest_between_sets_seconds', label: 'SetRst', min: 0, max: 600, suffix: 's' },
                                          ].map(({ key, label, min, max, suffix }) => (
                                            <div key={key} className="flex flex-col">
                                              <label className="text-[8px] text-[#64748B] uppercase tracking-widest font-bold text-center">{label}</label>
                                              <input
                                                type="number"
                                                min={min}
                                                max={max}
                                                value={t[key]}
                                                onChange={(e) => updateDrillTimer(di, did, { [key]: parseInt(e.target.value) || (min || 0) })}
                                                className="w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-md px-1 py-1.5 text-[#0A2242] text-xs text-center focus:outline-none focus:ring-2 focus:ring-[#FF9100]"
                                                data-testid={`timer-${key}-${did}`}
                                              />
                                            </div>
                                          ))}
                                          <p className="col-span-5 text-[9px] text-[#FF9100] bg-[#FF9100]/5 border border-[#FF9100]/20 rounded px-2 py-1 text-center">
                                            {t.sets}×{t.reps_per_set} · {t.work_seconds}s work{t.rest_between_reps_seconds > 0 && ` · ${t.rest_between_reps_seconds}s rep rest`} · {t.rest_between_sets_seconds}s set rest
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* Drill Picker — drills not yet added */}
                            <div>
                              <p className="text-[10px] uppercase tracking-widest text-[#64748B] font-bold mb-2">Add Drill</p>
                              <div className="space-y-1 max-h-48 overflow-y-auto">
                                {drills.length === 0 ? (
                                  <p className="text-xs text-[#64748B] py-2">Create drills first</p>
                                ) : (
                                  drills.filter(d => !day.drill_ids.includes(d.id)).map(drill => (
                                    <button key={drill.id} onClick={() => toggleDrill(di, drill.id)} className="w-full text-left flex items-center gap-2 p-2 rounded-lg text-sm text-[#64748B] hover:bg-[#F1F5F9] transition-colors">
                                      <div className="w-5 h-5 rounded border border-[#E2E8F0] flex items-center justify-center flex-shrink-0">
                                        <Plus size={12} className="text-[#64748B]" />
                                      </div>
                                      <span className="truncate">{drill.title}</span>
                                      <span className="text-[9px] text-[#64748B] ml-auto">{drill.category}</span>
                                    </button>
                                  ))
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <div>
                              <label className="text-[10px] uppercase tracking-widest text-[#64748B] font-bold mb-1 flex items-center gap-1">
                                <Brain size={10} className="text-[#A855F7]" /> Mental Prep Tip
                              </label>
                              <textarea
                                value={day.mental_tip}
                                onChange={e => updateDay(di, 'mental_tip', e.target.value)}
                                className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#0A2242] text-sm focus:outline-none focus:ring-2 focus:ring-[#A855F7] placeholder:text-[#64748B] resize-none h-20"
                                placeholder="e.g. Visualize your shots before you step on the ice..."
                              />
                            </div>
                            <div>
                              <label className="text-[10px] uppercase tracking-widest text-[#64748B] font-bold mb-1 flex items-center gap-1">
                                <Apple size={10} className="text-[#10B981]" /> Nutrition Tip
                              </label>
                              <textarea
                                value={day.nutrition_tip}
                                onChange={e => updateDay(di, 'nutrition_tip', e.target.value)}
                                className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#0A2242] text-sm focus:outline-none focus:ring-2 focus:ring-[#10B981] placeholder:text-[#64748B] resize-none h-20"
                                placeholder="e.g. Eat complex carbs 2 hours before training..."
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <button onClick={handleSave} disabled={saving} className="w-full bg-[#2563EB] hover:bg-[#3B82F6] text-white font-bold uppercase tracking-wide rounded-lg px-8 py-4 transition-colors disabled:opacity-50" data-testid="plan-save-btn">
                {saving ? 'Saving...' : editingId ? 'Update Plan' : 'Schedule Plan'}
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Plan List */}
        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : sortedPlans.length === 0 && !showForm ? (
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 text-center">
            <Calendar size={32} className="text-[#2563EB] mx-auto mb-3" />
            <p className="text-[#64748B] text-sm">No plans yet. Build your first weekly plan.</p>
            <p className="text-[#64748B] text-xs mt-1">Schedule weeks in advance — they'll release automatically.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {sortedPlans.map((plan, i) => {
              const current = isCurrentWeek(plan.start_date);
              const future = isFutureWeek(plan.start_date);
              return (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                  className={`bg-white border rounded-xl p-4 ${
                    current ? 'border-[#10B981]/50' : future ? 'border-[#2563EB]/30' : 'border-[#E2E8F0]'
                  }`}
                  data-testid={`plan-item-${i}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-['Barlow_Condensed'] text-sm font-bold">{plan.title}</h4>
                        {current && (
                          <span className="text-[9px] bg-[#10B981]/15 text-[#10B981] px-2 py-0.5 rounded-full font-bold uppercase tracking-widest">
                            Live Now
                          </span>
                        )}
                        {future && (
                          <span className="text-[9px] bg-[#2563EB]/15 text-[#2563EB] px-2 py-0.5 rounded-full font-bold uppercase tracking-widest">
                            Scheduled
                          </span>
                        )}
                        {!current && !future && (
                          <span className="text-[9px] bg-[#E2E8F0] text-[#64748B] px-2 py-0.5 rounded-full font-bold uppercase tracking-widest">
                            Past
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Calendar size={10} className="text-[#64748B]" />
                        <p className="text-[10px] text-[#64748B]">
                          {formatWeekLabel(plan.start_date)} &middot; {plan.days?.length || 0} days
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => handleEdit(plan)} className="p-2 text-[#64748B] hover:text-[#2563EB] transition-colors">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      </button>
                      <button onClick={() => handleDelete(plan.id)} className="p-2 text-[#64748B] hover:text-red-400 transition-colors"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
